import { EmailOutlined, PersonOutline, ShieldOutlined } from "@mui/icons-material";
import { Autocomplete, Box, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { COLORS } from "assets";
import { FormModalActions, MyModal, SelectInput, StyledInputLabel, TextInput } from "components";
import { ENDPOINTS, MEMBER_ROLES } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { FormEventHandler, useCallback, useEffect, useRef, useState } from "react";
import { MemberRole } from "types";
import { validateEmail, validateName } from "utils";
import { IRemoveSite, defaultState, initState } from "./types";

interface CandidateUser {
  _id: string;
  email: string;
  name: string;
}

interface IAddMember extends IRemoveSite {
  onClose: () => void;
}

const fieldSx = { height: 48, minHeight: 48 };
const iconSx = { fontSize: 20, color: COLORS.surface.secondaryText };

const autocompleteFieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: COLORS.surface.card,
    color: COLORS.surface.primaryText,
    borderRadius: `${COLORS.radius.md}px`,
    minHeight: 48,
    pl: 0.5,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.surface.border },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.surface.secondaryText },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.primary }
  },
  "& .MuiInputBase-input": {
    color: COLORS.surface.primaryText,
    py: 1.25
  },
  "& .MuiFormHelperText-root": {
    color: COLORS.surface.secondaryText,
    mx: 0.5
  },
  "& .MuiFormHelperText-root.Mui-error": {
    color: COLORS.error
  }
};

export default function AddMember({ site, onSuccess, onClose }: IAddMember) {
  const [state, setState] = useState(defaultState);
  const [errors, setErrors] = useState(initState);
  const [candidates, setCandidates] = useState<CandidateUser[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [nameFromDirectory, setNameFromDirectory] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const requestIdRef = useRef(0);
  const latestQueryRef = useRef("");
  const selectedEmailRef = useRef("");

  const t = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading, response, error, callFetch: callFetchPost } = useFetch("POST");
  const { callFetch: callFetchCandidates, cancelRequest: cancelCandidates } = useFetch<{ users?: CandidateUser[] }>("GET");

  const applyCandidate = useCallback((user: CandidateUser) => {
    selectedEmailRef.current = user.email.trim().toLowerCase();
    setState((prev) => ({
      ...prev,
      email: user.email,
      name: user.name || ""
    }));
    setErrors((prev) => ({
      ...prev,
      email: validateEmail(user.email),
      name: validateName(user.name || "")
    }));
    setNameFromDirectory(true);
  }, []);

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      if (!site) return;
      callFetchPost(ENDPOINTS.members, {
        body: JSON.stringify({
          email: state.email,
          name: state.name,
          role: state.role,
          siteId: site._id
        })
      }).catch(console.error);
    },
    [state, site, callFetchPost]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      onSuccess?.();
      onClose();
      setState(defaultState);
      setErrors(initState);
      setNameFromDirectory(false);
      selectedEmailRef.current = "";
    }
  }, [response, onSuccess, onClose, enqueueSnackbar]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
      if (error.data?.errors) {
        setErrors((prevState) => ({
          ...prevState,
          ...error.data.errors
        }));
      }
    }
  }, [error, enqueueSnackbar]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      cancelCandidates();
    };
  }, [cancelCandidates]);

  const fetchCandidates = useCallback(
    (query: string, immediate = false) => {
      if (!site?._id) return;
      latestQueryRef.current = query;
      if (debounceRef.current) clearTimeout(debounceRef.current);

      const run = () => {
        const requestId = ++requestIdRef.current;
        const trimmed = latestQueryRef.current.trim();
        cancelCandidates();
        setLoadingCandidates(true);
        const params = new URLSearchParams({
          siteId: site._id,
          limit: "25"
        });
        if (trimmed) params.set("search", trimmed);

        callFetchCandidates(`${ENDPOINTS.memberCandidates}?${params}`)
          .then((res) => {
            if (requestId !== requestIdRef.current) return;
            const users = res?.data?.users || [];
            setCandidates(users);
            const exact = users.find((u) => u.email.toLowerCase() === trimmed.toLowerCase());
            if (exact) {
              applyCandidate(exact);
            }
          })
          .catch(() => {
            if (requestId !== requestIdRef.current) return;
            setCandidates([]);
          })
          .finally(() => {
            if (requestId !== requestIdRef.current) return;
            setLoadingCandidates(false);
          });
      };

      if (immediate) {
        run();
      } else {
        debounceRef.current = setTimeout(run, 250);
      }
    },
    [site?._id, callFetchCandidates, cancelCandidates, applyCandidate]
  );

  const disabledSubmit = !!(errors.email || errors.name || !state.email || !state.name);

  return (
    <MyModal
      title={t["Add Member"]}
      subtitle={t["Add Member description"]}
      open={true}
      setOpen={onClose}
      variant="light"
    >
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2.5}>
          <Box>
            <StyledInputLabel>{t["Email"]}</StyledInputLabel>
            <Autocomplete
              freeSolo
              fullWidth
              open={open}
              onOpen={() => {
                setOpen(true);
                fetchCandidates(state.email, true);
              }}
              onClose={() => setOpen(false)}
              options={candidates}
              loading={loadingCandidates}
              filterOptions={(options) => options}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.email)}
              isOptionEqualToValue={(option, value) =>
                typeof option !== "string" && typeof value !== "string" && option._id === value._id
              }
              inputValue={state.email}
              onInputChange={(_, value, reason) => {
                if (reason === "reset") return;
                const diverged =
                  nameFromDirectory && value.trim().toLowerCase() !== selectedEmailRef.current;
                setState((prev) => ({
                  ...prev,
                  email: value,
                  ...(diverged ? { name: "" } : {})
                }));
                setErrors((prev) => ({
                  ...prev,
                  email: validateEmail(value),
                  ...(diverged ? { name: validateName("") } : {})
                }));
                if (diverged) {
                  selectedEmailRef.current = "";
                  setNameFromDirectory(false);
                }
                if (reason === "input") {
                  fetchCandidates(value);
                }
              }}
              onChange={(_, value) => {
                if (!value) return;
                if (typeof value === "string") {
                  const match = candidates.find((c) => c.email.toLowerCase() === value.trim().toLowerCase());
                  if (match) applyCandidate(match);
                  return;
                }
                applyCandidate(value);
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props as typeof props & { key?: string };
                return (
                  <Box component="li" key={option._id || key} {...optionProps}>
                    <Box>
                      <Typography variant="body2" sx={{ color: COLORS.surface.primaryText, fontWeight: 500 }}>
                        {option.email}
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.surface.secondaryText }}>
                        {option.name}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              ListboxProps={{
                style: { maxHeight: 280 }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  required
                  type="email"
                  placeholder={t["Search existing users"]}
                  helperText={errors?.email || " "}
                  error={!!errors?.email}
                  sx={autocompleteFieldSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Box
                          sx={{
                            pl: 1,
                            display: "flex",
                            alignItems: "center",
                            color: COLORS.surface.secondaryText,
                            flexShrink: 0
                          }}
                        >
                          <EmailOutlined sx={iconSx} />
                        </Box>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {loadingCandidates ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Box>

          <TextInput
            value={state.name}
            label={t["Name"]}
            type="text"
            placeholder={t["e.g John Doe"]}
            error={!!errors.name}
            helperText={
              nameFromDirectory ? t["Filled from selected user"] : errors.name
            }
            disabled={nameFromDirectory}
            startAdornment={<PersonOutline sx={iconSx} />}
            sx={fieldSx}
            onChange={(e) => {
              setState((prevState) => ({
                ...prevState,
                name: e.target.value
              }));
              setErrors((prevState) => ({
                ...prevState,
                name: validateName(e.target.value)
              }));
            }}
            fullWidth
            required
          />

          <SelectInput
            fullWidth
            data={MEMBER_ROLES}
            id="role-select"
            value={state.role}
            label={t["Role"]}
            helperText=""
            startAdornment={<ShieldOutlined sx={iconSx} />}
            onChange={(e) => {
              setState((prevState) => ({
                ...prevState,
                role: e.target.value as MemberRole
              }));
            }}
          />
        </Stack>

        <FormModalActions
          onCancel={onClose}
          cancelLabel={t["Cancel"]}
          submitLabel={t["Add Member"]}
          loading={isLoading}
          disabled={disabledSubmit}
        />
      </Box>
    </MyModal>
  );
}
